import {
    createComponentById,
    NO_PATTERN_BUSINESS_LOGIC,
} from '@vfde-brix/ws10/core';
import {
    createIconText,
    IconText,
} from '@vfde-brix/ws10/icon-text';
import { StateProps } from 'Container/App/interface';
import { getTauschbonusAmount } from '@vfde-sails/page-options';
import { replacePlaceholders } from '@vfde-sails/utils';
import {
    GIGADEPOT_ICON_TEXT_CONTAINER,
    RESTLAUFZEIT_ICON_TEXT_CONTAINER,
    TAUSCHBONUS_PROMO_CONTAINER,
    TRADE_IN_PROMO_CONTAINER,
} from 'Container/App/constants';
import {
    selectCustomerConfig,
    selectIsEligibleForRestlaufzeit,
} from 'Container/App/slice';
import { startAppListening } from 'Container/App/listener';
import { selectDataVolume } from 'Container/App/selectors';

/**
 * Mount Restlaufzeit icon text
 */
export const mountRestlaufzeitIconText = (): IconText | null => {
    const iconText = createComponentById(createIconText, RESTLAUFZEIT_ICON_TEXT_CONTAINER, NO_PATTERN_BUSINESS_LOGIC);

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectIsEligibleForRestlaufzeit(currentState) !== selectIsEligibleForRestlaufzeit(previousState),
        effect: (_action, api) => {
            const isEligibleForRestlaufzeit = selectIsEligibleForRestlaufzeit(api.getState());

            iconText?.toggleContainer(!isEligibleForRestlaufzeit);
        },
    });

    return iconText;
};

/**
 * Mount Trade in icon text
 */
export const mountTradeInIconText = (): IconText | null => {
    const iconText = createComponentById(createIconText, TRADE_IN_PROMO_CONTAINER, NO_PATTERN_BUSINESS_LOGIC);

    startAppListening({
        predicate: (_action, currentState, previousState) => {
            const currentCustomerConfig = selectCustomerConfig(currentState);
            const previousCustomerConfig = selectCustomerConfig(previousState);

            return currentCustomerConfig.hasTradeIn !== previousCustomerConfig.hasTradeIn
                || currentCustomerConfig.hasTauschbonus !== previousCustomerConfig.hasTauschbonus;
        },
        effect: (_action, api) => {
            const { hasTradeIn, hasTauschbonus } = selectCustomerConfig(api.getState());

            iconText?.toggleContainer(!hasTradeIn || hasTauschbonus);
        },
    });

    return iconText;
};

/**
 * Mount Tauschbonus icon text
 */
export const mountTauschbonusIconText = (): IconText | null => {
    const iconText = createComponentById(createIconText, TAUSCHBONUS_PROMO_CONTAINER, NO_PATTERN_BUSINESS_LOGIC);

    startAppListening({
        predicate: (_action, currentState, previousState) => {
            const currentCustomerConfig = selectCustomerConfig(currentState);
            const previousCustomerConfig = selectCustomerConfig(previousState);

            return currentCustomerConfig.hasTradeIn !== previousCustomerConfig.hasTradeIn
                || currentCustomerConfig.hasTauschbonus !== previousCustomerConfig.hasTauschbonus;
        },
        effect: (_action, api) => {
            const { hasTradeIn, hasTauschbonus, deviceId } = selectCustomerConfig(api.getState());

            if (hasTauschbonus) {
                iconText && updateTauschbonusIconText(iconText, deviceId);
            }

            iconText?.toggleContainer(!hasTradeIn || !hasTauschbonus);
        },
    });

    return iconText;
};

/**
 * Mount GigaDepot icon text
 */
export const mountGigaDepotIconText = (): IconText | null => {
    const iconText = createComponentById(createIconText, GIGADEPOT_ICON_TEXT_CONTAINER, NO_PATTERN_BUSINESS_LOGIC);

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectDataVolume(currentState) !== selectDataVolume(previousState),
        effect: (_action, api) => {
            const dataVolume = selectDataVolume(api.getState());

            iconText?.toggleContainer(dataVolume?.unlimited || false);
        },
    });

    return iconText;
};

/**
 * Update tauschbonus icon text with the correct amount
 */
export const updateTauschbonusIconText = (tauschbonusIconText: IconText, deviceId: StateProps['customerConfig']['deviceId']) => {
    const discount = getTauschbonusAmount(deviceId!);
    tauschbonusIconText.update({
        txtContent: replacePlaceholders(tauschbonusIconText.getPropertyByKey('txtContent'), {
            tauschbonusValue: `${discount}`,
        }),
    });
};
